import { useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import { SaveIcon } from '@app/components/icons';
import {
  Credential,
  CredentialAssignment,
  CredentialAssignmentCreate,
  CredentialAssignmentUpdate,
  CredentialAssignmentAllOfScopeOneOfTypeEnum,
  CredentialAssignmentAllOfScopeOneOf1TypeEnum,
  CredentialAssignmentAllOfScopeOneOf2TypeEnum,
  Device,
  Site,
  Vendorschema
} from '@app/types/sdk';
import { useCredentialAssignmentsApi } from '@app/hooks/useApis';

type ScopeType = 'org' | 'sites' | 'devices';

export interface AssignmentDrawerProps {
  orgId: string;
  open: boolean;
  mode: 'create' | 'edit';
  assignmentId?: string | null;
  initialAssignment?: CredentialAssignment | null;
  defaultCredentialId?: string | null;
  credentialOptions: Credential[];
  siteOptions: Site[];
  deviceOptions: Device[];
  existingAssignments: CredentialAssignment[];
  onClose: () => void;
  onSaved: (assignment: CredentialAssignment, etag: string | null) => void;
}

interface MatchState {
  hostnameRegex: string;
  hostnamePrefixes: string;
  hostnameSuffixes: string;
  labelInput: string;
  vendorsAny: string;
  modelsAny: string;
}

const defaultMatchState: MatchState = {
  hostnameRegex: '',
  hostnamePrefixes: '',
  hostnameSuffixes: '',
  labelInput: '',
  vendorsAny: '',
  modelsAny: ''
};

const parseList = (input: string): string[] =>
  input
    .split(/[,|\r?\n]/)
    .map((token) => token.trim())
    .filter(Boolean);

const VENDOR_VALUES = Object.values(Vendorschema);

const normalizeVendors = (input: string): { vendors: Vendorschema[]; invalid: string[] } => {
  const vendors: Vendorschema[] = [];
  const invalid: string[] = [];
  parseList(input).forEach((token) => {
    const match = VENDOR_VALUES.find((value) => value.toLowerCase() === token.toLowerCase());
    if (match) {
      vendors.push(match);
      return;
    }
    invalid.push(token);
  });
  return { vendors, invalid };
};

const parseLabels = (input: string): { labels: Record<string, string>; error: string | null } => {
  if (!input.trim()) {
    return { labels: {}, error: null };
  }
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const labels: Record<string, string> = {};
  for (const line of lines) {
    const [key, ...rest] = line.split('=');
    const trimmedKey = key?.trim();
    const value = rest.join('=').trim();
    if (!trimmedKey || !value) {
      return { labels: {}, error: `Label "${line}" must be key=value.` };
    }
    labels[trimmedKey] = value;
  }
  return { labels, error: null };
};

const AssignmentDrawer = ({
  orgId,
  open,
  mode,
  assignmentId,
  initialAssignment,
  defaultCredentialId,
  credentialOptions,
  siteOptions,
  deviceOptions,
  existingAssignments,
  onClose,
  onSaved
}: AssignmentDrawerProps) => {
  const assignmentsApi = useCredentialAssignmentsApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignment, setAssignment] = useState<CredentialAssignment | null>(initialAssignment ?? null);
  const [etag, setEtag] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [priority, setPriority] = useState(100);
  const [credentialId, setCredentialId] = useState<string>('');
  const [scopeType, setScopeType] = useState<ScopeType>('org');
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [matchState, setMatchState] = useState<MatchState>(defaultMatchState);
  const [matchError, setMatchError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const vendorOptions = useMemo(() => VENDOR_VALUES, []);

  const resetState = useCallback(() => {
    const record = initialAssignment ?? null;
    setAssignment(record);
    setEtag(null);
    setName(record?.name ?? '');
    setDescription(record?.description ?? '');
    setEnabled(record?.enabled ?? true);
    setPriority(record?.priority ?? 100);
    setCredentialId(record?.credentialId ?? defaultCredentialId ?? credentialOptions[0]?.id ?? '');
    setScopeType(resolveScopeType(record));
    setSelectedSites(resolveSiteScope(record));
    setSelectedDevices(resolveDeviceScope(record));
    setMatchState(resolveMatchState(record));
    setMatchError(null);
    setSaving(false);
    setSaveError(null);
  }, [credentialOptions, defaultCredentialId, initialAssignment]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'create' || !assignmentId) {
      resetState();
      return;
    }
    let isActive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await assignmentsApi.orgsOrgIdCredentialAssignmentsAssignmentIdGetRaw({
          orgId,
          assignmentId
        });
        if (!isActive) {
          return;
        }
        const record = (await response.raw.json()) as CredentialAssignment;
        const responseEtag = response.raw.headers.get('ETag');
        setAssignment(record);
        setEtag(responseEtag);
        setName(record.name ?? '');
        setDescription(record.description ?? '');
        setEnabled(record.enabled);
        setPriority(record.priority ?? 100);
        setCredentialId(record.credentialId);
        setScopeType(resolveScopeType(record));
        setSelectedSites(resolveSiteScope(record));
        setSelectedDevices(resolveDeviceScope(record));
        setMatchState(resolveMatchState(record));
      } catch (caught) {
        console.error('Failed to load assignment', caught);
        if (isActive) {
          setError(caught instanceof Error ? caught.message : 'Failed to load assignment.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [assignmentId, assignmentsApi, mode, open, orgId, resetState]);

  const selectedCredential = useMemo(() => credentialOptions.find((item) => item.id === credentialId) ?? null, [credentialId, credentialOptions]);
  const knownSiteIds = useMemo(
    () => new Set(siteOptions.map((site) => site.id).filter((id): id is string => typeof id === 'string' && id.trim().length > 0)),
    [siteOptions]
  );
  const knownDeviceIds = useMemo(
    () => new Set(deviceOptions.map((device) => device.id).filter((id): id is string => typeof id === 'string' && id.trim().length > 0)),
    [deviceOptions]
  );
  const selectedSiteIdsKnown = useMemo(() => filterKnownIds(selectedSites, knownSiteIds), [knownSiteIds, selectedSites]);
  const selectedDeviceIdsKnown = useMemo(() => filterKnownIds(selectedDevices, knownDeviceIds), [knownDeviceIds, selectedDevices]);
  const selectedSiteCount = selectedSiteIdsKnown.length;
  const selectedDeviceCount = selectedDeviceIdsKnown.length;

  const scopeDescription = useMemo(() => {
    if (scopeType === 'org') {
      return 'Organization-wide';
    }
    if (scopeType === 'sites' && selectedSiteCount === 0) {
      return 'No sites selected';
    }
    if (scopeType === 'sites') {
      return `${selectedSiteCount} site(s)`;
    }
    if (selectedDeviceCount === 0) {
      return 'No devices selected';
    }
    return `${selectedDeviceCount} device(s)`;
  }, [scopeType, selectedDeviceCount, selectedSiteCount]);

  const previewDevices: Device[] = [];
  const conflictAssignments: CredentialAssignment[] = [];

  const handleSave = async () => {
    if (!credentialId) {
      setSaveError('Select a credential to apply.');
      return;
    }
    const labelsResult = parseLabels(matchState.labelInput);
    if (labelsResult.error) {
      setMatchError(labelsResult.error);
      return;
    }
    setMatchError(null);
    const vendorResult = normalizeVendors(matchState.vendorsAny);
    if (vendorResult.invalid.length > 0) {
      setMatchError(`Unknown vendor(s): ${vendorResult.invalid.join(', ')}`);
      return;
    }
    const match = buildMatch(matchState, labelsResult.labels, vendorResult.vendors);
    const scope = buildScope(scopeType, selectedSiteIdsKnown, selectedDeviceIdsKnown);
    if (!scope) {
      setSaveError('Select a scope for this assignment.');
      return;
    }
    const payload = {
      name: name.trim() || undefined,
      description: description.trim() || undefined,
      enabled,
      priority,
      credentialId,
      scope,
      match
    };

    setSaving(true);
    setSaveError(null);
    try {
      if (mode === 'create') {
        const response = await assignmentsApi.orgsOrgIdCredentialAssignmentsPostRaw({
          orgId,
          credentialAssignmentCreate: payload as CredentialAssignmentCreate
        });
        const record = (await response.raw.json()) as CredentialAssignment;
        const responseEtag = response.raw.headers.get('ETag');
        onSaved(record, responseEtag);
      } else if (assignmentId) {
        const response = await assignmentsApi.orgsOrgIdCredentialAssignmentsAssignmentIdPatchRaw({
          orgId,
          assignmentId,
          ifMatch: etag ?? undefined,
          credentialAssignmentUpdate: payload as CredentialAssignmentUpdate
        });
        const record = (await response.raw.json()) as CredentialAssignment;
        const responseEtag = response.raw.headers.get('ETag');
        onSaved(record, responseEtag);
      }
    } catch (caught) {
      console.error('Failed to save assignment', caught);
      setSaveError(caught instanceof Error ? caught.message : 'Failed to save assignment.');
    } finally {
      setSaving(false);
    }
  };

  const drawerActions = (
    <div className="drawer-icon-actions">
      <button
        type="button"
        className="drawer-icon-button"
        aria-label="Save assignment"
        title="Save assignment"
        onClick={handleSave}
        disabled={saving}
      >
        <SaveIcon size={18} />
      </button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'New assignment' : assignment?.name ?? 'Assignment'}
      subtitle="Control which devices receive each credential."
      width="wide"
      actions={drawerActions}
    >
      {loading ? (
        <p>Loading assignment…</p>
      ) : error ? (
        <p className="org-shell__banner" role="alert">
          {error}
        </p>
      ) : (
        <div className="form-grid">
          <div className="record-summary">
            <div className="record-summary__grid">
              <div>
                <span>Credential</span>
                <strong>{selectedCredential?.name ?? credentialId ?? '—'}</strong>
              </div>
              <div>
                <span>Priority</span>
                <strong>{priority}</strong>
              </div>
              <div>
                <span>Scope</span>
                <strong>{scopeDescription}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{enabled ? 'Enabled' : 'Disabled'}</strong>
              </div>
            </div>
          </div>
          <section className="section-card">
            <h3 className="section-card__title">Basics</h3>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Name</span>
                <input className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Edge switches" />
              </label>
              <label className="form-field">
                <span className="form-field__label">Description</span>
                <input className="input" value={description} onChange={(event) => setDescription(event.target.value)} />
              </label>
            </div>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Credential</span>
                <select className="select" value={credentialId} onChange={(event) => setCredentialId(event.target.value)}>
                  {credentialOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name ?? option.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <span className="form-field__label">Priority</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={1000}
                  value={priority}
                  onChange={(event) => setPriority(Number(event.target.value) || 100)}
                />
              </label>
            </div>
            <label className="checkbox-row">
              <input className="checkbox" type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
              Assignment enabled
            </label>
            {selectedCredential && (
              <p className="helper-text">
                Targets devices with credential: <strong>{selectedCredential.name ?? selectedCredential.id}</strong>
              </p>
            )}
          </section>
          <section className="section-card">
            <h3 className="section-card__title">Scope</h3>
            <div className="form-grid form-grid--two">
              <label className="radio-row">
                <input type="radio" className="radio" value="org" checked={scopeType === 'org'} onChange={() => setScopeType('org')} />
                Entire organization
              </label>
              <label className="radio-row">
                <input type="radio" className="radio" value="sites" checked={scopeType === 'sites'} onChange={() => setScopeType('sites')} />
                Specific sites
              </label>
              <label className="radio-row">
                <input type="radio" className="radio" value="devices" checked={scopeType === 'devices'} onChange={() => setScopeType('devices')} />
                Exact devices
              </label>
            </div>
            {scopeType === 'sites' && (
              <div className="multi-column">
                {siteOptions.map((site) => (
                  <label key={site.id} className="checkbox-row">
                    <input
                      className="checkbox"
                      type="checkbox"
                      checked={!!site.id && selectedSites.includes(site.id)}
                      onChange={(event) => {
                        const siteId = site.id;
                        if (!siteId) {
                          return;
                        }
                        setSelectedSites((prev) => {
                          if (event.target.checked) {
                            return prev.includes(siteId) ? prev : [...prev, siteId];
                          }
                          return prev.filter((id) => id !== siteId);
                        });
                      }}
                    />
                    {site.name ?? site.id}
                  </label>
                ))}
              </div>
            )}
            {scopeType === 'devices' && (
              <div className="multi-column">
                {deviceOptions.map((device) => (
                  <label key={device.id} className="checkbox-row">
                    <input
                      className="checkbox"
                      type="checkbox"
                      checked={!!device.id && selectedDevices.includes(device.id)}
                      onChange={(event) => {
                        const deviceId = device.id;
                        if (!deviceId) {
                          return;
                        }
                        setSelectedDevices((prev) => {
                          if (event.target.checked) {
                            return prev.includes(deviceId) ? prev : [...prev, deviceId];
                          }
                          return prev.filter((id) => id !== deviceId);
                        });
                      }}
                    />
                    {device.displayName ?? device.mgmtAddress ?? device.id}
                  </label>
                ))}
              </div>
            )}
            <p className="helper-text">Current selection: {scopeDescription}</p>
          </section>
          <section className="section-card">
            <h3 className="section-card__title">Filters (optional)</h3>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Hostname regex</span>
                <input
                  className="input"
                  value={matchState.hostnameRegex}
                  onChange={(event) => setMatchState((prev) => ({ ...prev, hostnameRegex: event.target.value }))}
                  placeholder="^core-"
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Hostname prefixes</span>
                <input
                  className="input"
                  value={matchState.hostnamePrefixes}
                  onChange={(event) => setMatchState((prev) => ({ ...prev, hostnamePrefixes: event.target.value }))}
                  placeholder="edge-,agg-"
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Hostname suffixes</span>
                <input
                  className="input"
                  value={matchState.hostnameSuffixes}
                  onChange={(event) => setMatchState((prev) => ({ ...prev, hostnameSuffixes: event.target.value }))}
                  placeholder="-01,-02"
                />
              </label>
            </div>
            <label className="form-field">
              <span className="form-field__label">Required labels (key=value per line)</span>
              <textarea
                className="textarea"
                value={matchState.labelInput}
                onChange={(event) => setMatchState((prev) => ({ ...prev, labelInput: event.target.value }))}
                placeholder="role=edge"
              />
            </label>
            <div className="form-grid form-grid--two">
              <label className="form-field">
                <span className="form-field__label">Vendors any</span>
                <select
                  className="select"
                  multiple
                  value={normalizeVendors(matchState.vendorsAny).vendors}
                  onChange={(event) => {
                    const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                    setMatchState((prev) => ({ ...prev, vendorsAny: selected.join(', ') }));
                  }}
                >
                  {vendorOptions.map((vendor) => (
                    <option key={vendor} value={vendor}>
                      {vendor}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <span className="form-field__label">Models any</span>
                <input
                  className="input"
                  value={matchState.modelsAny}
                  onChange={(event) => setMatchState((prev) => ({ ...prev, modelsAny: event.target.value }))}
                  placeholder="C9300,7280R"
                />
              </label>
            </div>
            {matchError && (
              <p className="org-shell__banner" role="alert">
                {matchError}
              </p>
            )}
          </section>
          {saveError && <div className="form-error">{saveError}</div>}
        </div>
      )}
    </Drawer>
  );
};

const extractScopeType = (scope: any): string => {
  if (!scope) {
    return '';
  }
  if (typeof scope.type === 'string') {
    return scope.type;
  }
  if (scope.type && typeof scope.type === 'object' && typeof scope.type.type === 'string') {
    return scope.type.type;
  }
  return '';
};

const resolveScopeType = (assignment: CredentialAssignment | null | undefined): ScopeType => {
  if (!assignment) {
    return 'org';
  }
  const scope = assignment.scope as any;
  const type = extractScopeType(scope);
  if (!type || type === CredentialAssignmentAllOfScopeOneOfTypeEnum.Org) {
    return 'org';
  }
  if (type === CredentialAssignmentAllOfScopeOneOf1TypeEnum.Sites) {
    return 'sites';
  }
  return 'devices';
};

const resolveSiteScope = (assignment: CredentialAssignment | null | undefined): string[] => {
  if (!assignment || resolveScopeType(assignment) !== 'sites') {
    return [];
  }
  const scope = assignment.scope as { siteIds?: string[] };
  if (Array.isArray(scope.siteIds)) {
    return scope.siteIds;
  }
  // handle legacy objects that nest the ids
  if (scope && typeof (scope as any).siteIds === 'object' && Array.isArray((scope as any).siteIds?.value)) {
    return (scope as any).siteIds.value;
  }
  return [];
};

const resolveDeviceScope = (assignment: CredentialAssignment | null | undefined): string[] => {
  if (!assignment || resolveScopeType(assignment) !== 'devices') {
    return [];
  }
  const scope = assignment.scope as { deviceIds?: string[] };
  if (Array.isArray(scope.deviceIds)) {
    return scope.deviceIds;
  }
  if (scope && typeof (scope as any).deviceIds === 'object' && Array.isArray((scope as any).deviceIds?.value)) {
    return (scope as any).deviceIds.value;
  }
  return [];
};

const resolveMatchState = (assignment: CredentialAssignment | null | undefined): MatchState => {
  if (!assignment || !assignment.match) {
    return defaultMatchState;
  }
  const hostname = assignment.match.hostname;
  return {
    hostnameRegex: hostname?.regex ?? '',
    hostnamePrefixes: hostname?.prefixAny?.join(', ') ?? '',
    hostnameSuffixes: hostname?.suffixAny?.join(', ') ?? '',
    labelInput: assignment.match.labelsAll
      ? Object.entries(assignment.match.labelsAll)
          .map(([k, v]) => `${k}=${v}`)
          .join('\n')
      : '',
    vendorsAny: assignment.match.vendorsAny?.join(', ') ?? '',
    modelsAny: assignment.match.modelsAny?.join(', ') ?? ''
  };
};

const scopeProxyFlag = Symbol('scopeProxyPatched');

const dedupeIds = (ids: string[]) => Array.from(new Set(ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)));
const filterKnownIds = (ids: string[], knownIds: Set<string>): string[] => dedupeIds(ids).filter((id) => knownIds.has(id));

// Work around upstream ScopeToJSON bug that strips site/device IDs by tricking its type guard order.
const withScopeSerializationHack = <T extends CredentialAssignment['scope']>(scope: T): T => {
  if (!scope || typeof scope !== 'object') {
    return scope;
  }
  const typed = scope as T & { [scopeProxyFlag]?: boolean };
  if (typed[scopeProxyFlag] || scope.type === CredentialAssignmentAllOfScopeOneOfTypeEnum.Org) {
    return scope;
  }
  let hasSkippedTypeCheck = false;
  const proxy = new Proxy(scope, {
    has(target, prop) {
      if (prop === 'type') {
        if (!hasSkippedTypeCheck) {
          hasSkippedTypeCheck = true;
          return false;
        }
      }
      return Reflect.has(target, prop);
    },
    get(target, prop, receiver) {
      return Reflect.get(target, prop, receiver);
    },
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      return Reflect.getOwnPropertyDescriptor(target, prop);
    }
  }) as T & { [scopeProxyFlag]?: boolean };
  proxy[scopeProxyFlag] = true;
  return proxy;
};

const buildScope = (scopeType: ScopeType, siteIds: string[], deviceIds: string[]) => {
  if (scopeType === 'org') {
    return { type: CredentialAssignmentAllOfScopeOneOfTypeEnum.Org } as CredentialAssignment['scope'];
  }
  if (scopeType === 'sites') {
    const normalized = dedupeIds(siteIds);
    if (normalized.length === 0) {
      return null;
    }
    return withScopeSerializationHack({
      type: CredentialAssignmentAllOfScopeOneOf1TypeEnum.Sites,
      siteIds: normalized
    } as CredentialAssignment['scope']);
  }
  const normalized = dedupeIds(deviceIds);
  if (normalized.length === 0) {
    return null;
  }
  return withScopeSerializationHack({
    type: CredentialAssignmentAllOfScopeOneOf2TypeEnum.Devices,
    deviceIds: normalized
  } as CredentialAssignment['scope']);
};

const buildMatch = (state: MatchState, labels: Record<string, string>, vendors: Vendorschema[]) => {
  const prefixes = parseList(state.hostnamePrefixes);
  const suffixes = parseList(state.hostnameSuffixes);
  const regex = state.hostnameRegex.trim();
  const hostname =
    regex || prefixes.length > 0 || suffixes.length > 0
      ? {
          regex: regex || undefined,
          prefixAny: prefixes.length > 0 ? prefixes : undefined,
          suffixAny: suffixes.length > 0 ? suffixes : undefined
        }
      : undefined;
  const models = parseList(state.modelsAny);
  return {
    hostname,
    labelsAll: Object.keys(labels).length > 0 ? labels : undefined,
    vendorsAny: vendors.length > 0 ? vendors : undefined,
    modelsAny: models.length > 0 ? models : undefined
  };
};

const matchesPreview = (
  device: Device,
  scope: ScopeType,
  siteIds: string[],
  deviceIds: string[],
  state: MatchState
): boolean => {
  if (scope === 'sites' && !siteIds.includes(device.siteId ?? '')) {
    return false;
  }
  if (scope === 'devices' && !deviceIds.includes(device.id ?? '')) {
    return false;
  }
  const hostname = (device.displayName ?? device.mgmtAddress ?? '').toLowerCase();
  if (state.hostnameRegex) {
    try {
      const regex = new RegExp(state.hostnameRegex);
      if (!regex.test(device.displayName ?? device.mgmtAddress ?? '')) {
        return false;
      }
    } catch {
      // ignore invalid regex preview
    }
  }
  const prefixAny = parseList(state.hostnamePrefixes).map((value) => value.toLowerCase());
  if (prefixAny.length > 0 && !prefixAny.some((prefix) => hostname.startsWith(prefix))) {
    return false;
  }
  const suffixAny = parseList(state.hostnameSuffixes).map((value) => value.toLowerCase());
  if (suffixAny.length > 0 && !suffixAny.some((suffix) => hostname.endsWith(suffix))) {
    return false;
  }
  const labelResult = parseLabels(state.labelInput);
  if (!labelResult.error && Object.keys(labelResult.labels).length > 0) {
    const labels = device.labels ?? {};
    const matched = Object.entries(labelResult.labels).every(([key, value]) => labels[key] === value);
    if (!matched) {
      return false;
    }
  }
  const vendors = normalizeVendors(state.vendorsAny).vendors.map((value) => value.toLowerCase());
  if (vendors.length > 0 && (!device.vendor || !vendors.includes(device.vendor.toLowerCase()))) {
    return false;
  }
  const models = parseList(state.modelsAny).map((value) => value.toLowerCase());
  if (models.length > 0 && (!device.model || !models.includes(device.model.toLowerCase()))) {
    return false;
  }
  return true;
};

const overlaps = (
  assignment: CredentialAssignment,
  scopeType: ScopeType,
  siteIds: string[],
  deviceIds: string[]
): boolean => {
  const otherScope = resolveScopeType(assignment);
  if (scopeType === 'org' || otherScope === 'org') {
    return true;
  }
  if (scopeType === 'sites' && otherScope === 'sites') {
    const otherSites = new Set(resolveSiteScope(assignment));
    return siteIds.some((id) => otherSites.has(id));
  }
  if (scopeType === 'devices' && otherScope === 'devices') {
    const otherDevices = new Set(resolveDeviceScope(assignment));
    return deviceIds.some((id) => otherDevices.has(id));
  }
  if (scopeType === 'sites' && otherScope === 'devices') {
    const otherDevices = resolveDeviceScope(assignment);
    return otherDevices.length > 0;
  }
  if (scopeType === 'devices' && otherScope === 'sites') {
    const otherSites = resolveSiteScope(assignment);
    return otherSites.length > 0;
  }
  return false;
};

export default AssignmentDrawer;
